import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { Masked, MaskedTeam } from "work/masked-fields";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

export function getFeeScheduleTeamMembers(
  team: VendorTeam | MaskedTeam | null | undefined,
): EntityMember[] {
  if (!Masked.isPresentNotMasked(team)) return [];
  const members = [team.leader, ...team.members].filter(
    (member): member is EntityMember => Boolean(member?.userId?.value && member?.legalEntityId?.value),
  );
  const seenMemberIds = new Set<string>();
  return members.filter((member) => {
    const memberId = `${member.userId.value}:${member.legalEntityId.value}`;
    if (seenMemberIds.has(memberId)) return false;
    seenMemberIds.add(memberId);
    return true;
  });
}

/**
 * The same team, with the people a fee schedule template named seated on it.
 *
 * This is the "add them to the team" answer to the question `useReconcileFeeScheduleStaffing`
 * asks. Anyone already on the team is left alone, and so is anyone who is already its leader: a
 * person leads a vendor team or belongs to it and never both, and `VendorTeam.ValidateInputAsync`
 * rejects a team that breaks that with an opaque 400. See `VendorTeam.isLedBy`.
 */
export function withFeeScheduleStaffingSeated(
  team: VendorTeam,
  members: readonly EntityMember[],
): VendorTeam {
  const additions = members.filter(
    (member) =>
      !team.isLedBy(member) &&
      !team.members.some((existing) => existing.isSamePersonAs(member)),
  );
  if (additions.length === 0) return team;
  return new VendorTeam({
    leader: team.leader,
    members: [...team.members, ...additions],
    marketplaceId: team.marketplaceId,
  });
}

/**
 * The marketplace team a proposal's vendor team came from, if it came from one.
 *
 * What it unlocks is the team's advertised rates as a source for the proposal's fee schedule
 * (issue #5113). Undefined in the two cases where there is nothing to offer: a vendor team assembled
 * by hand rather than picked off the marketplace carries no `marketplaceId`, and a team masked to
 * this viewer is not a team whose profile they may read.
 */
export function getMarketplaceTeamId(
  team: VendorTeam | MaskedTeam | null | undefined,
): Guid | undefined {
  if (!Masked.isPresentNotMasked(team)) return undefined;
  return team.marketplaceId;
}
